/**
 * Calculate average daily spending over a specified period
 * @param {Array} transactions - List of transaction objects
 * @param {Date} startDate - Start date for calculation (optional, defaults to the earliest transaction)
 * @param {Date} endDate - End date for calculation (optional, defaults to today)
 * @param {Boolean} includeCredits - Whether to include credits in calculation (optional, defaults to false)
 * @return {Object} Object containing average daily spending and related statistics
 */
export function calculateAverageDailySpending(transactions, startDate = null, endDate = null, includeCredits = false) {
    let transactionsCopy = JSON.parse(JSON.stringify(transactions));

    // Filter to only include debits (expenses) by default
    let filteredTransactions = includeCredits
        ? transactionsCopy
        : transactionsCopy.filter(t => t.type === 'debit');

    filteredTransactions = filteredTransactions.map(t => {
        if (t.type === 'debit' && t.amount > 0) {
            t.amount = t.amount * -1;
        }
        return t;
    });

    if (filteredTransactions.length === 0) {
        return {
            averageDailySpending: 0,
            totalSpending: 0,
            daysCovered: 0,
            startDate: startDate || new Date(),
            endDate: endDate || new Date()
        };
    }

    // Sort transactions by date
    filteredTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Determine date range
    const firstTransactionDate = new Date(filteredTransactions[0].date);
    const today = new Date();

    const calculationStartDate = startDate ? new Date(startDate) : firstTransactionDate;
    const calculationEndDate = endDate ? new Date(endDate) : today;

    // Calculate total spending
    const totalSpending = filteredTransactions
        .filter(t => {
            const transactionDate = new Date(t.date);
            // Normalize dates to compare only date parts, not time
            const transactionDateOnly = new Date(transactionDate.getFullYear(), transactionDate.getMonth(), transactionDate.getDate());
            const startDateOnly = new Date(calculationStartDate.getFullYear(), calculationStartDate.getMonth(), calculationStartDate.getDate());
            const endDateOnly = new Date(calculationEndDate.getFullYear(), calculationEndDate.getMonth(), calculationEndDate.getDate());
            
            return transactionDateOnly >= startDateOnly && transactionDateOnly <= endDateOnly;
        })
        .reduce((sum, t) => sum + t.amount, 0);

    // Calculate number of days in the period
    const daysCovered = Math.max(1, Math.ceil(
        (calculationEndDate - calculationStartDate) / (1000 * 60 * 60 * 24)
    ));

    // Calculate average daily spending
    const averageDailySpending = totalSpending / daysCovered;

    return {
        averageDailySpending,
        totalSpending,
        daysCovered,
        startDate: calculationStartDate,
        endDate: calculationEndDate
    };
}